import EndCall from '@/assets/images/EndCall.svg';
import MicOn from '@/assets/images/Mic.svg';
import MicOff from '@/assets/images/MicMute.svg';
import Video from '@/assets/images/Video.svg';
import VideoOff from '@/assets/images/VideoOff.svg';
import { CustomDialog } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { showToast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { UPDATE_SESSION } from '@/pages/sessions/GraphQl/mutation';
import { Role, SessionStatus } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useMutation, useReactiveVar } from '@apollo/client';
import { AudioChangeAction, MutedSource } from '@zoom/videosdk';
import { truncate } from 'lodash';
import { Camera, ChevronUp, CircleHelp, Mic, Volume2 } from 'lucide-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ZoomMediaContext from '../../../context/media-context';
import ZoomContext from '../../../context/zoom-context';
import { useUnmount } from '../../../hooks';
import { sessionVar } from '../meetingVar';
import { SELF_VIDEO_ID } from '../video-constants';
import './video-footer.css';
interface VideoFooterProps {
  className?: string;
  selfShareCanvas?: HTMLCanvasElement | HTMLVideoElement | null;
  sharing?: boolean;
}

const isAudioEnable = typeof AudioWorklet === 'function';

const VideoFooter = (props: VideoFooterProps) => {
  const { id } = useParams();
  const sessionData = useReactiveVar(sessionVar);
  console.log('sessionData', sessionData);
  const { className } = props;
  const { userDetails } = useReactiveVar(authState);
  const zmClient = useContext(ZoomContext);
  const { mediaStream } = useContext(ZoomMediaContext);
  const [updateSession] = useMutation(UPDATE_SESSION, {
    refetchQueries: ['GetAllSession', 'GetSessionById', 'GetAllSessionsCount'],
  });

  const [openLeave, setOpenLeave] = useState<'end' | 'leave' | ''>('');
  const [isStartedAudio, setIsStartedAudio] = useState(
    zmClient.getCurrentUserInfo() && zmClient.getCurrentUserInfo().audio !== '',
  );
  const [isStartedVideo, setIsStartedVideo] = useState(zmClient.getCurrentUserInfo()?.bVideoOn);
  const isBlur = false;
  const [isMuted, setIsMuted] = useState(
    zmClient.getCurrentUserInfo()?.muted === undefined
      ? true
      : !!zmClient.getCurrentUserInfo()?.muted,
  );

  const activePlaybackUrl = '';

  const onCameraClick = useCallback(async () => {
    if (isStartedVideo) {
      await mediaStream?.stopVideo();
      setIsStartedVideo(false);
    } else {
      const startVideoOptions = {
        hd: true,
        fullHd: true,
        ptz: mediaStream?.isBrowserSupportPTZ(),
        originalRatio: true,
      };
      if (mediaStream?.isSupportVirtualBackground() && isBlur) {
        Object.assign(startVideoOptions, { virtualBackground: { imageUrl: 'blur' } });
      }
      await mediaStream?.startVideo(startVideoOptions);
      if (!mediaStream?.isSupportMultipleVideos()) {
        const canvasElement = document.querySelector(`#${SELF_VIDEO_ID}`) as HTMLCanvasElement;
        mediaStream?.renderVideo(
          canvasElement,
          zmClient.getSessionInfo().userId,
          canvasElement?.width,
          canvasElement?.height,
          0,
          0,
          3,
        );
      }
      setIsStartedVideo(true);
    }
  }, [mediaStream, isStartedVideo, zmClient, isBlur]);

  const onMicrophoneClick = useCallback(async () => {
    if (isStartedAudio) {
      if (isMuted) {
        await mediaStream?.unmuteAudio();
      } else {
        await mediaStream?.muteAudio();
      }
    } else {
      try {
        if (activePlaybackUrl) {
          await mediaStream?.startAudio({ mediaFile: { url: activePlaybackUrl, loop: true } });
        } else {
          await mediaStream?.startAudio({ highBitrate: true });
        }
      } catch (e: any) {
        if (e.type === 'INSUFFICIENT_PRIVILEGES' && e.reason === 'USER_FORBIDDEN_MICROPHONE') {
          showToast({
            message: 'Please enable microphone permission',
            variant: 'error',
          });
        }
        console.warn(e);
      }
    }
  }, [mediaStream, isStartedAudio, isMuted]);

  const onHostAudioMuted = useCallback(
    (payload: any) => {
      const { action, source } = payload;
      if (action === AudioChangeAction.Join) {
        setIsStartedAudio(true);
        setTimeout(() => {
          setIsMuted(!!zmClient.getCurrentUserInfo()?.muted);
        }, 1000);
      } else if (action === AudioChangeAction.Leave) {
        setIsStartedAudio(false);
      } else if (action === AudioChangeAction.Muted) {
        setIsMuted(true);
        if (source === MutedSource.PassiveByMuteOne) {
          console.log('Host muted you');
        }
      } else if (action === AudioChangeAction.Unmuted) {
        setIsMuted(false);
        if (source === 'passive') {
          console.log('Host unmuted you');
        }
      }
    },
    [zmClient],
  );

  const onLeaveClick = useCallback(async () => {
    await zmClient.leave();
    window.location.replace('/');
  }, [zmClient]);

  const onEndClick = useCallback(async () => {
    if (userDetails?.role === Role.Coach || userDetails?.role === Role.Trainer) {
      await updateSession({
        variables: {
          data: {
            _id: id as string,
            programId: sessionData?.programId,
            ...(sessionData?.sportId && { sportId: sessionData?.sportId }),
            sessionStatus: SessionStatus.Completed,
          },
        },
      });
      await zmClient.leave();
      window.location.replace(`/feedback/${id}`);
    } else {
      await zmClient.leave();
      window.location.replace(`/feedback/${id}`);
    }
  }, [zmClient]);

  useEffect(() => {
    zmClient.on('current-audio-change', onHostAudioMuted);
    return () => {
      zmClient.off('current-audio-change', onHostAudioMuted);
    };
  }, [zmClient, onHostAudioMuted]);

  useUnmount(() => {
    if (zmClient.getSessionInfo().isInMeeting) {
      if (isStartedAudio) {
        mediaStream?.stopAudio();
      }
      if (isStartedVideo) {
        mediaStream?.stopVideo();
      }
      mediaStream?.stopShareScreen();
    }
  });

  useEffect(() => {
    if (zmClient?.getCurrentUserInfo()?.muted) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
    if (zmClient?.getCurrentUserInfo()?.bVideoOn) {
      setIsStartedVideo(true);
    } else {
      setIsStartedVideo(false);
    }
  }, [zmClient.getCurrentUserInfo()]);

  const [audioInputDevices, setAudioInputDevices] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [activeMicrophone, setActiveMicrophone] = useState('');
  const [activeSpeaker, setActiveSpeaker] = useState('');
  const [cameraDevices, setCameraDevices] = useState<Array<{ value: string; label: string }>>([]);
  const [activeCamera, setActiveCamera] = useState('');

  // Add this useEffect hook to initialize and listen for device changes
  useEffect(() => {
    const updateAllDevices = async () => {
      try {
        // First get microphone permissions (this will also request camera permissions)
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true });

        // Update camera devices
        const cameras = mediaStream?.getCameraList() || [];
        const formattedCameras = cameras.map((item) => ({
          value: item.deviceId,
          label: item.label || `Camera ${cameras.indexOf(item) + 1}`,
        }));
        setCameraDevices(formattedCameras);

        // Update microphone devices
        const mics = mediaStream?.getMicList() || [];
        const formattedMics = mics
          .map((item) => ({
            value: item.deviceId,
            label: item.label.startsWith('Default - ')
              ? item.label.substring('Default - '.length)
              : item.label || `Microphone ${mics.indexOf(item) + 1}`,
          }))
          .filter(
            (device, index, self) => index === self.findIndex((d) => d.label === device.label),
          );
        setAudioInputDevices(formattedMics);

        // Update speaker devices
        const speakers = mediaStream?.getSpeakerList() || [];
        const formattedSpeakers = speakers
          .map((item) => ({
            value: item.deviceId,
            label: item.label.startsWith('Default - ')
              ? item.label.substring('Default - '.length)
              : item.label || `Speaker ${speakers.indexOf(item) + 1}`,
          }))
          .filter(
            (device, index, self) => index === self.findIndex((d) => d.label === device.label),
          );
        setAudioOutputDevices(formattedSpeakers);

        // Set active devices if not set
        if (!activeCamera && formattedCameras.length > 0) {
          const currentActiveCamera = mediaStream?.getActiveCamera();
          setActiveCamera(
            !currentActiveCamera || currentActiveCamera === 'default'
              ? formattedCameras[0].value
              : currentActiveCamera,
          );
        }
        if (!activeMicrophone && formattedMics.length > 0) {
          const currentActiveMic = mediaStream?.getActiveMicrophone();
          setActiveMicrophone(
            !currentActiveMic || currentActiveMic === 'default'
              ? formattedMics[0].value
              : currentActiveMic,
          );
        }
        if (!activeSpeaker && formattedSpeakers.length > 0) {
          const currentActiveSpeaker = mediaStream?.getActiveSpeaker();
          setActiveSpeaker(
            !currentActiveSpeaker || currentActiveSpeaker === 'default'
              ? formattedSpeakers[0].value
              : currentActiveSpeaker,
          );
        }
      } catch (error) {
        console.error('Error updating devices:', error);
      }
    };

    // Initial load
    updateAllDevices();

    // Set up listeners for device changes
    const handleDeviceChange = () => {
      updateAllDevices();
    };

    // Standard device change listener
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    // Zoom-specific approach - listen to relevant events
    zmClient.on('device-change', handleDeviceChange);
    zmClient.on('media-sdk-change', handleDeviceChange);

    // Cleanup
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      zmClient.off('device-change', handleDeviceChange);
      zmClient.off('media-sdk-change', handleDeviceChange);
    };
  }, [mediaStream, zmClient]);

  const handleMicChange = async (value: string) => {
    try {
      await mediaStream?.switchMicrophone(value);
      setActiveMicrophone(value);
    } catch (error) {
      console.error('Error switching microphone:', error);
      showToast({
        message: 'Failed to switch microphone',
        variant: 'error',
      });
    }
  };

  // Update the handleSpeakerChange function
  const handleSpeakerChange = async (value: string) => {
    try {
      await mediaStream?.switchSpeaker(value);
      setActiveSpeaker(value);
    } catch (error) {
      console.error('Error switching speaker:', error);
      showToast({
        message: 'Failed to switch speaker',
        variant: 'error',
      });
    }
  };

  const handleCameraChange = async (value: string) => {
    try {
      await mediaStream?.switchCamera(value);
      setActiveCamera(value);
    } catch (error) {
      console.error('Error switching camera:', error);
      showToast({
        message: 'Failed to switch camera',
        variant: 'error',
      });
    }
  };

  return (
    <div className={cn('video-footer relative flex gap-5 pb-2', className)}>
      <span className='absolute top-[-80px] text-sm font-normal flex items-center gap-1 px-3 py-2 rounded-full shadow-lg bg-black/50'>
        For Support call{' '}
        <a className='flex items-center gap-2 font-medium' href='tel:+16468872690'>
          +1 (646) 887-2690
          <CircleHelp className='w-6 h-6' />
        </a>
      </span>

      <div className='flex items-center border rounded-full'>
        <Popover>
          <PopoverTrigger asChild>
            <Button id='mic-more-btn' className='' variant='ghost' size='icon'>
              <ChevronUp />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side='top'
            sideOffset={15}
            className='grid grid-cols-2 gap-4 w-[600px] max-w-[95%] bg-background p-2 rounded-full'
          >
            <Select onValueChange={handleMicChange} value={activeMicrophone}>
              <SelectTrigger className='w-full justify-start gap-2 rounded-full device_select_trigger'>
                <Mic /> <SelectValue placeholder='Select microphone' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {audioInputDevices?.map((device) => (
                    <SelectItem key={device.value} value={device.value} className='min-w-[280px]'>
                      {truncate(device.label, { length: 40 })}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <Select onValueChange={handleSpeakerChange} value={activeSpeaker}>
              <SelectTrigger className='w-full justify-start gap-2 rounded-full device_select_trigger'>
                <Volume2 /> <SelectValue placeholder='Select speaker' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {audioOutputDevices?.map((device) => (
                    <SelectItem key={device.value} value={device.value} className='min-w-[280px]'>
                      {truncate(device.label, { length: 40 })}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </PopoverContent>
        </Popover>
        {isAudioEnable && (
          <Button
            id='mic-btn'
            variant={'outline'}
            size={'icon'}
            className='bg-[#4A494980] w-12 h-12  hover:bg-[#4a4949bb]'
            onClick={onMicrophoneClick}
          >
            {isMuted ? (
              <img src={MicOff} className='w-8 h-8' alt='mic off' />
            ) : (
              <img src={MicOn} className='w-8 h-8' alt='mic on' />
            )}
          </Button>
        )}
      </div>
      <div className='flex items-center border rounded-full'>
        <Popover>
          <PopoverTrigger asChild>
            <Button id='mic-more-btn' className='' variant='ghost' size='icon'>
              <ChevronUp />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side='top'
            sideOffset={15}
            className='grid grid-cols-1 gap-4 w-[400px] max-w-[95%] bg-background p-2 rounded-full'
          >
            <Select onValueChange={handleCameraChange} value={activeCamera}>
              <SelectTrigger className='w-full justify-start gap-4 rounded-full device_select_trigger'>
                <Camera /> <SelectValue placeholder='Select camera' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {cameraDevices?.map((device) => (
                    <SelectItem key={device.value} value={device.value} className='min-w-[280px]'>
                      {truncate(device.label, { length: 40 })}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </PopoverContent>
        </Popover>
        <Button
          id='video-btn'
          variant={'outline'}
          size={'icon'}
          className='bg-[#4A494980] w-12 h-12 hover:bg-[#4a4949bb]'
          onClick={onCameraClick}
        >
          {isStartedVideo ? (
            <img src={Video} className='w-8 h-8' alt='video on' />
          ) : (
            <img src={VideoOff} className='w-8 h-8' alt='video off' />
          )}
        </Button>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            id='session-leave-btn'
            variant='outline'
            size='icon'
            className='rounded-full w-24 h-12 bg-[#EA584C] hover:bg-red'
          >
            <img src={EndCall} className='h-8 w-8' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='min-w-36 border-gray-600'>
          <DropdownMenuItem
            onClick={() => setOpenLeave('end')}
            className='cursor-pointer focus:bg-bgSecondary focus:text-white'
          >
            End Session
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setOpenLeave('leave')}
            className='cursor-pointer focus:bg-bgSecondary focus:text-white'
          >
            Leave & Come Back
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {openLeave ? (
        <CustomDialog
          open={!!openLeave}
          desc={
            userDetails?.role === Role.Trainee
              ? `Are you sure you want to ${openLeave === 'end' ? 'end' : 'leave'} session now?`
              : `Are you sure you want to ${openLeave === 'end' ? 'end' : 'leave'} this session now? ${openLeave === 'end' ? 'Once ended, the session will be marked as complete, and neither you nor the customer will be able to rejoin.' : ''}`
          }
          onCancel={() => setOpenLeave('')}
          title={`${openLeave === 'end' ? 'End' : 'Leave'} Session?`}
          onConfirm={openLeave === 'end' ? onEndClick : onLeaveClick}
        />
      ) : null}
    </div>
  );
};
export default VideoFooter;
